import { ItemAdditionalOptions } from "../BaseList";
import { ListBaseProps } from "../List";
import { SortingListItemModel } from "../SortingListTypes";

export const sortingIsActive = (item: SortingListItemModel) => item.reverse != null;

export const getListHeightImp = (
    items: any[],
    itemHeight: number,
    getItemHeight: ListBaseProps<any>["getItemHeight"],
    maxHeight: number
) => {
    if (!getItemHeight) {
        return Math.min(items.length * itemHeight, maxHeight);
    }

    let result = 0;

    for (let i = 0; i < items.length; i++) {
        result += getItemHeight(items[i], i, items);
        if (result >= maxHeight) {
            return result;
        }
    }

    return result;
};

type Props<TItem> = Pick<ListBaseProps<TItem>, "items" | "getItemKey"> & { focusedItem?: TItem };

/**
 * Returns actual focused item and its index in passed items.
 * Tries to find item by key, not by reference, if key getter is specified.
 * Useful for lists where item's instance changes when (un)selected.
 */
export const getFocused = <TItem extends {}>({ items, focusedItem, getItemKey }: Props<TItem>) => {
    if (!focusedItem) {
        return { focusedIndex: -1, focusedItem: undefined };
    }

    let focusedIndex = items.indexOf(focusedItem);

    if (focusedIndex < 0 && !!getItemKey) {
        const focusedItemKey = getItemKey(focusedItem);
        // for some items getItemKey might be nothing (e.g. Invite user/Add Unnamed Resource)
        // in this case instead of finding the last focused item we'll found the first item
        // without key, that's wrong. In this case it's better to lose the focused item.
        // So this check fixes focusing invite user when user clicks on add unnamed resource in
        // users select control.
        if (focusedItemKey) {
            focusedIndex = items.findIndex(item => getItemKey(item) === focusedItemKey);
        }
    }

    return { focusedIndex, focusedItem: items[focusedIndex] };
};

/**
 * Filters empty groups in list after search.
 */
export const emptyGroup = <T extends ItemAdditionalOptions>(item: T, index: number, array: T[]) =>
    !item.group || // not a group
    (index !== array.length - 1 && // group is not the last item in list
        !array[index + 1].group); // next item is not a group

export const isSelectable = (item: ItemAdditionalOptions) => !item.group;
export const isAddRow = <ItemType>(item: ItemType & ItemAdditionalOptions<ItemType>) => !!item.addRow;
